import { useCallback, useState } from "react";

import { useApolloClient, useMutation } from "@apollo/client";
import { useIntl } from "react-intl";

import { useBlizzardEvents } from "../../analytics/hooks/useBlizzardEvents";
import { type PreviewMediaResource, type Visibility } from "../../gql-generated/types";
import { getLens } from "../../lens/api/queries";
import {
    createWebLensUpload as createWebLensUploadMutation,
    renameWebLens as renameWebLensMutation,
    updateWebLens as updateWebLensMutation,
} from "../api/mutations";
import {
    type CreateWebLensUploadMutation,
    type CreateWebLensUploadMutationVariables,
} from "../api/mutations/createWebLensUpload.gql-generated";
import {
    type RenameWebLensMutation,
    type RenameWebLensMutationVariables,
} from "../api/mutations/renameWebLens.gql-generated";
import {
    type UpdateWebLensMutation,
    type UpdateWebLensMutationVariables,
} from "../api/mutations/updateWebLens.gql-generated";
import { WebLensUploadError, putToSignedUrl } from "../api/putToSignedUrl";
import { messages } from "../components/messages.generated";
import { type SubmissionCheck, type WebLensSubmitResult } from "../model";
import { describeWebLensCheckLabel } from "../utils/describeWebLensCheckLabel";
import { type WebLensBuildOutputMeasurement } from "../utils/measureWebLensBuildOutput";
import {
    captureWebLensException,
    failedCheckReason,
    startWebLensAttempt,
    uploadFailureReason,
} from "../utils/webLensSentry";
import { preChecksPassed, runClientPreChecks } from "../validation/preChecks";

export interface UpdateWebLensInput {
    file: File;
    /** The web lens being updated — the id on the lens page URL / submit result. */
    lensId: string;
    /** Replacement icon; null keeps the current one (Lens Central inherits it). */
    logo: File | null;
    /** New lens name; null keeps the current one. */
    name: string | null;
    /** Mark the lens as a game with this update; false keeps the current marking (this maskless path cannot unmark). */
    markAsGame: boolean;
    /**
     * Replacement previews in display order, kept ones by mediaId and new ones by uploadRefId.
     * Null or empty keeps the lens' current previews — the maskless update cannot clear them.
     */
    previewMediaResources: PreviewMediaResource[] | null;
    /** Page-level budget measurement; the budget row must not vanish when update() takes the rail. */
    buildOutput: WebLensBuildOutputMeasurement | null;
    /** Omitted or false keeps the stored enrollment; only true requests it. */
    lensCreatorPayoutEnrolled?: boolean;
    /** Omitted keeps the current visibility. */
    visibility?: Visibility;
}

export interface RenameWebLensInput {
    lensId: string;
    /** The new name; the page only calls rename() when it differs from the current one. */
    name: string;
    /** Omitted or false keeps the stored enrollment; only true requests it. */
    lensCreatorPayoutEnrolled?: boolean;
    /** Omitted keeps the current visibility. */
    visibility?: Visibility;
}

interface UpdateWebLensState {
    checks: SubmissionCheck[];
    result: WebLensSubmitResult | null;
    updating: boolean;
    update: (input: UpdateWebLensInput) => Promise<void>;
    /** Package-less update via updateLens: no upload; re-reviewed, not re-converted. */
    rename: (input: RenameWebLensInput) => Promise<void>;
    reset: () => void;
}

/**
 * The update sibling of useSubmitWebLens (ticket → signed-URL PUT → mutation, optional logo), plus
 * rename() for a package-less rename plus optional visibility/rewards edits. Visibility, payout
 * enrollment and previews travel only when explicitly edited; the folder stays unchanged.
 */
export const useUpdateWebLens = (): UpdateWebLensState => {
    const intl = useIntl();
    const client = useApolloClient();
    const [checks, setChecks] = useState<SubmissionCheck[]>([]);
    const [result, setResult] = useState<WebLensSubmitResult | null>(null);
    const [uploading, setUploading] = useState(false);
    const [executeCreateUpload] = useMutation<CreateWebLensUploadMutation, CreateWebLensUploadMutationVariables>(
        createWebLensUploadMutation,
    );
    const [executeUpdate, { loading: updating }] = useMutation<UpdateWebLensMutation, UpdateWebLensMutationVariables>(
        updateWebLensMutation,
    );
    const [executeRename, { loading: renaming }] = useMutation<RenameWebLensMutation, RenameWebLensMutationVariables>(
        renameWebLensMutation,
    );

    const { sendBlizzardEvent } = useBlizzardEvents();
    const update = useCallback(
        async ({
            file,
            lensId,
            logo,
            name,
            markAsGame,
            previewMediaResources,
            buildOutput,
            lensCreatorPayoutEnrolled,
            visibility,
        }: UpdateWebLensInput) => {
            setResult(null);
            // Attempt-owned instrumentation — see startWebLensAttempt for the outcome contract.
            const { trace: flowTrace, sendOutcome: sendOutcomeEvent } = startWebLensAttempt(
                "update",
                sendBlizzardEvent,
                { payout_enrolled: lensCreatorPayoutEnrolled, game_lens: markAsGame },
            );

            // Same message-ownership rule as submission: raw GraphQL/transport errors go to
            // Sentry and the creator sees a localized fallback; server validation rows render
            // verbatim as BFF-owned creator copy.
            const describeUploadFailure = (error: unknown): string => {
                if (error instanceof WebLensUploadError) {
                    if (error.kind === "file_changed") {
                        return intl.formatMessage(messages.uploadFailedFileChanged);
                    }
                    if (error.kind === "stalled") {
                        return intl.formatMessage(messages.uploadFailedStalled);
                    }
                    if (error.kind === "http") {
                        return intl.formatMessage(messages.uploadFailedHttp, { status: error.status });
                    }
                    return intl.formatMessage(messages.uploadFailedNetwork);
                }
                captureWebLensException(error, "upload");
                return intl.formatMessage(messages.genericFailure);
            };

            const uploadCheck = (state: SubmissionCheck["state"], detail?: string): SubmissionCheck => ({
                id: "direct-upload",
                label: intl.formatMessage(messages.uploadCheckLabel),
                state,
                detail,
            });

            const serverCheck = (state: SubmissionCheck["state"], detail?: string): SubmissionCheck => ({
                id: "server",
                label: intl.formatMessage(messages.updateCheckLabel),
                state,
                detail,
            });

            const preChecks = runClientPreChecks(intl, file, logo, buildOutput);
            if (!preChecksPassed(preChecks)) {
                setChecks(preChecks);
                sendOutcomeEvent("web_lens_update_failed_precheck", failedCheckReason(preChecks));
                return;
            }

            setUploading(true);
            const endUploadStage = flowTrace.startStage("upload");
            let directUpload: { packageRef: string; logoUploaded: boolean };
            try {
                setChecks([...preChecks, uploadCheck("running")]);
                const ticketResponse = await executeCreateUpload();
                const ticket = ticketResponse.data?.createWebLensUpload;
                if (!ticket) {
                    captureWebLensException(
                        ticketResponse.errors?.[0] ?? new Error("createWebLensUpload returned no ticket"),
                        "upload",
                    );
                    setChecks([...preChecks, uploadCheck("fail", intl.formatMessage(messages.genericFailure))]);
                    sendOutcomeEvent("web_lens_update_failed_upload", "no_ticket");
                    return;
                }
                await putToSignedUrl(ticket.packageUploadUrl, file, ticket.packageMaxBytes, (fraction) => {
                    setChecks([...preChecks, uploadCheck("running", `${Math.round(fraction * 100)}%`)]);
                });
                if (logo) {
                    await putToSignedUrl(ticket.logoUploadUrl, logo, ticket.logoMaxBytes);
                }
                directUpload = { packageRef: ticket.uploadRef, logoUploaded: Boolean(logo) };
            } catch (error) {
                setChecks([...preChecks, uploadCheck("fail", describeUploadFailure(error))]);
                sendOutcomeEvent("web_lens_update_failed_upload", uploadFailureReason(error));
                return;
            } finally {
                endUploadStage();
                setUploading(false);
            }

            const baseChecks: SubmissionCheck[] = [...preChecks, uploadCheck("pass")];
            setChecks([...baseChecks, serverCheck("running")]);

            flowTrace.startStage("mutation");
            try {
                const response = await executeUpdate({
                    variables: {
                        input: {
                            lensId,
                            packageRef: directUpload.packageRef,
                            // Required by the schema: false already means "keep", so the flag
                            // carries meaning in every state (per BFF review).
                            logoUploaded: directUpload.logoUploaded,
                            // The rename is included only when the creator actually renamed —
                            // omitting keeps the current name without the BFF having to compare.
                            ...(name === null ? {} : { name }),
                            ...(lensCreatorPayoutEnrolled === undefined ? {} : { lensCreatorPayoutEnrolled }),
                            ...(visibility === undefined ? {} : { visibility }),
                            // Marking only — omitting keeps the current marking (rollout-safe
                            // against a BFF predating the field, same shape as submission).
                            ...(markAsGame ? { isGameUserProvided: true } : {}),
                            // Sent only when the creator actually changed the previews: an absent
                            // list is what tells Lens Central to keep the lens' current ones.
                            ...(previewMediaResources && previewMediaResources.length > 0
                                ? { previewMediaResources }
                                : {}),
                        },
                    },
                });

                const payload = response.data?.updateWebLens;
                if (!payload) {
                    captureWebLensException(
                        response.errors?.[0] ?? new Error("updateWebLens returned an empty response"),
                        "update",
                    );
                    setChecks([...baseChecks, serverCheck("fail", intl.formatMessage(messages.genericFailure))]);
                    sendOutcomeEvent("web_lens_update_failed_server", "empty_payload");
                    return;
                }

                const serverChecks: SubmissionCheck[] = payload.validation.map((check) => ({
                    id: check.name,
                    label: describeWebLensCheckLabel(intl, check.name),
                    state: check.passed ? "pass" : "fail",
                    detail: check.detail ?? undefined,
                }));
                setChecks([...baseChecks, ...serverChecks]);

                if (payload.lensId) {
                    sendOutcomeEvent("web_lens_update_success");
                    // The lens is already in the cache — the page that sent the creator here read
                    // it, and the picker listed it — and the lens page reads cache-first, so
                    // without a refresh the hand-off would land on the pre-update status, name and
                    // icon, reading as though the update did nothing. Refreshing rather than
                    // writing status by hand because an update can change the name and icon too.
                    // Deliberately the lean getLens document (not getLensInfo, whose unrelated
                    // organizations read could fail this) and deliberately non-fatal: the update
                    // has already succeeded, and a failed refresh must not report it as failed —
                    // worst case the lens page corrects itself on its own next fetch.
                    try {
                        await client.query({ query: getLens, variables: { lensId }, fetchPolicy: "network-only" });
                    } catch (refreshError) {
                        captureWebLensException(refreshError, "update");
                    }
                    setResult({ lensId: payload.lensId, status: payload.status ?? undefined });
                } else {
                    sendOutcomeEvent("web_lens_update_failed_server", failedCheckReason(serverChecks));
                }
            } catch (error) {
                captureWebLensException(error, "update");
                setChecks([...baseChecks, serverCheck("fail", intl.formatMessage(messages.genericFailure))]);
                sendOutcomeEvent("web_lens_update_failed_server", "transport");
            }
        },
        [client, executeCreateUpload, executeUpdate, intl, sendBlizzardEvent],
    );

    const rename = useCallback(
        async ({ lensId, name, lensCreatorPayoutEnrolled, visibility }: RenameWebLensInput) => {
            setResult(null);
            const { trace: flowTrace, sendOutcome: sendOutcomeEvent } = startWebLensAttempt(
                "rename",
                sendBlizzardEvent,
            );

            const renameCheck = (state: SubmissionCheck["state"], detail?: string): SubmissionCheck => ({
                id: "rename",
                label: intl.formatMessage(messages.renameCheckLabel),
                state,
                detail,
            });

            // A blank name is a caller bug: the page validates before calling.
            const newName = name.trim();
            if (newName.length === 0) {
                setChecks([renameCheck("fail", intl.formatMessage(messages.nameErrorRequired))]);
                sendOutcomeEvent("web_lens_rename_failed_precheck", "blank_name");
                return;
            }

            setChecks([renameCheck("running")]);
            flowTrace.startStage("mutation");
            try {
                const response = await executeRename({
                    variables: {
                        lensId,
                        lensName: newName,
                        // Null is GraphQL's absent optional input here, so unchanged fields retain
                        // their server values while the generated variables remain exact.
                        creatorRewardProgramEnrolled: lensCreatorPayoutEnrolled ?? null,
                        profileVisibility: visibility ?? null,
                    },
                });
                const lens = response.data?.updateLens.lens;
                if (!lens) {
                    captureWebLensException(
                        response.errors?.[0] ?? new Error("renameWebLens returned an empty response"),
                        "rename",
                    );
                    setChecks([renameCheck("fail", intl.formatMessage(messages.renameFailed))]);
                    sendOutcomeEvent("web_lens_rename_failed_server", "empty_payload");
                    return;
                }
                setChecks([renameCheck("pass", lens.name)]);
                sendOutcomeEvent("web_lens_rename_success");

                // Same cache-first hand-off reason as update(); non-fatal.
                try {
                    await client.query({ query: getLens, variables: { lensId }, fetchPolicy: "network-only" });
                } catch (refreshError) {
                    captureWebLensException(refreshError, "rename");
                }
                setResult({ lensId: lens.id, status: lens.status });
            } catch (error) {
                // Most likely the lens is locked (a version still processing); the copy says to retry.
                captureWebLensException(error, "rename");
                setChecks([renameCheck("fail", intl.formatMessage(messages.renameFailed))]);
                sendOutcomeEvent("web_lens_rename_failed_server", "transport");
            }
        },
        [client, executeRename, intl, sendBlizzardEvent],
    );

    const reset = useCallback(() => {
        setChecks([]);
        setResult(null);
    }, []);

    return { checks, result, updating: updating || uploading || renaming, update, rename, reset };
};
